import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api';

export type Customer = {
  id: number;
  custNum: string;
  name: string;
  city: string | null;
  province: string | null;
  address: string | null;
  isWalkIn: boolean;
};

export type OrderLine = {
  id: number;
  productId: number;
  lineCode: string;
  partNumber: string;
  description: string;
  uom: string;
  unitPrice: string;
  qty: number;
  discountAmount: string;
  lineTotal: string;
  promoLabel: string | null;
};

export type Order = {
  id: number;
  number: string | null;
  status: 'DRAFT' | 'SUBMITTED' | 'CANCELLED';
  poNumber: string | null;
  notes: string | null;
  subtotal: string;
  discountTotal: string;
  total: string;
  submittedAt: string | null;
  customer: Customer;
  lines: OrderLine[];
};

export type OrderSummary = {
  id: number;
  number: string | null;
  status: Order['status'];
  total: string;
  createdAt: string;
  customerName: string;
  custNum: string;
};

/**
 * Identifiant du panier en cours, gardé en localStorage.
 *
 * Le panier fait autorité côté serveur ; ce n'est qu'un fil d'Ariane. Sur un
 * iPad qui se verrouille, une PWA que Safari décharge de la mémoire ou un
 * rechargement accidentel, il ramène le rep sur SA commande plutôt que sur un
 * écran vide — sans jamais servir de source de vérité pour les montants.
 */
const CLE_PANIER = 'supershow.panier';

export function memoriserPanier(orderId: number) {
  try {
    localStorage.setItem(CLE_PANIER, String(orderId));
  } catch {
    // Mode privé ou stockage plein : on continue sans filet, ce n'est pas bloquant.
  }
}

export function panierMemorise(): number | null {
  try {
    const v = localStorage.getItem(CLE_PANIER);
    const n = v ? Number(v) : NaN;
    return Number.isInteger(n) && n > 0 ? n : null;
  } catch {
    return null;
  }
}

export function oublierPanier() {
  try {
    localStorage.removeItem(CLE_PANIER);
  } catch {
    /* sans conséquence */
  }
}

/** Mémorise le panier courant tant qu'il est un brouillon. */
export function useSouvenirDuPanier(order: Order | undefined) {
  useEffect(() => {
    if (!order) return;
    if (order.status === 'DRAFT') memoriserPanier(order.id);
    else oublierPanier();
  }, [order?.id, order?.status]);
}

// --- Requêtes --------------------------------------------------------------

export function useCustomers(q: string) {
  return useQuery<{ customers: Customer[]; total: number }>({
    queryKey: ['customers', q],
    queryFn: () => api(`/api/customers?q=${encodeURIComponent(q)}&limit=30`),
    staleTime: 60_000,
  });
}

export function useOrder(id: number | null) {
  return useQuery<{ order: Order }>({
    queryKey: ['order', id],
    queryFn: () => api(`/api/orders/${id}`),
    enabled: id !== null,
  });
}

export function useOrders() {
  return useQuery<{ orders: OrderSummary[]; submittedCount: number; submittedTotal: string }>({
    queryKey: ['orders'],
    queryFn: () => api('/api/orders'),
  });
}

/**
 * Toutes les mutations du panier renvoient la commande entière recalculée par
 * le serveur, qu'on réinjecte directement dans le cache : les totaux affichés
 * sont toujours ceux du serveur, jamais un calcul refait côté client.
 */
function useMutationPanier(orderId: number) {
  const qc = useQueryClient();
  return {
    onSuccess: (data: { order: Order }) => {
      qc.setQueryData(['order', orderId], data);
      void qc.invalidateQueries({ queryKey: ['orders'] });
    },
  };
}

export function useAjouterAuPanier(orderId: number) {
  const commun = useMutationPanier(orderId);
  return useMutation({
    mutationFn: (body: { productId: number; qty?: number }) =>
      api<{ order: Order }>(`/api/orders/${orderId}/lines`, {
        method: 'POST',
        body: JSON.stringify({ qty: 1, ...body }),
      }),
    ...commun,
  });
}

export function useChangerQuantite(orderId: number) {
  const commun = useMutationPanier(orderId);
  return useMutation({
    mutationFn: ({ lineId, qty }: { lineId: number; qty: number }) =>
      api<{ order: Order }>(`/api/orders/${orderId}/lines/${lineId}`, {
        method: 'PATCH',
        body: JSON.stringify({ qty }),
      }),
    ...commun,
  });
}

export function useEnregistrerEntete(orderId: number) {
  const commun = useMutationPanier(orderId);
  return useMutation({
    mutationFn: (body: { poNumber?: string | null; notes?: string | null }) =>
      api<{ order: Order }>(`/api/orders/${orderId}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    ...commun,
  });
}

export function useSoumettre(orderId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api<{ order: Order; number: string }>(`/api/orders/${orderId}/submit`, { method: 'POST' }),
    onSuccess: (data) => {
      qc.setQueryData(['order', orderId], { order: data.order });
      void qc.invalidateQueries({ queryKey: ['orders'] });
      oublierPanier();
    },
  });
}

export function useCreerCommande() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (customerId: number) =>
      api<{ order: Order }>('/api/orders', { method: 'POST', body: JSON.stringify({ customerId }) }),
    onSuccess: (data) => {
      qc.setQueryData(['order', data.order.id], data);
      memoriserPanier(data.order.id);
    },
  });
}

export function useCreerWalkIn() {
  return useMutation({
    mutationFn: (body: { name: string; city?: string; contactName?: string; phone?: string }) =>
      api<{ customer: Customer }>('/api/customers/walk-in', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
  });
}
